import { Loader2, ShoppingCart, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export const AssignSalesTab = ({
  formData, customer, unassignedLoading, unassignedSales,
  selectedSalesToAssign, assigningSales, convertToAccount,
  setConvertToAccount, showAllSales, setShowAllSales,
  salesSearch, setSalesSearch, loadUnassignedSales,
  handleAssignSales, toggleSaleSelection, selectAllSales,
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Assign Sales to Customer</h3>
          <p className="text-sm text-gray-500">
            Find and assign sales to {formData.name || customer?.name}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadUnassignedSales} disabled={unassignedLoading}>
          <RefreshCcw className={`h-4 w-4 mr-1 ${unassignedLoading ? 'animate-spin' : ''}`} />
          Search
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <label className="text-sm text-gray-600">Search by Transaction #</label>
          <Input
            placeholder="e.g. SALE-000123"
            value={salesSearch}
            onChange={(e) => setSalesSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadUnassignedSales()}
          />
        </div>
        <div className="flex items-center gap-2 pb-2">
          <input
            type="checkbox"
            id="showAllSales"
            checked={showAllSales}
            onChange={(e) => {
              setShowAllSales(e.target.checked);
            }}
            className="h-4 w-4"
          />
          <label htmlFor="showAllSales" className="text-sm cursor-pointer">
            Include sales already assigned to other customers
          </label>
        </div>
      </div>

      {/* Options */}
      <div className="flex items-center gap-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <input
          type="checkbox"
          id="convertToAccount"
          checked={convertToAccount}
          onChange={(e) => setConvertToAccount(e.target.checked)}
          className="h-4 w-4"
        />
        <label htmlFor="convertToAccount" className="text-sm cursor-pointer">
          <span className="font-medium">Convert to Account Sale</span>
          <span className="text-amber-700 ml-1">
            (Add to customer's account balance - they will owe this amount)
          </span>
        </label>
      </div>

      {/* Selection summary and assign button */}
      {selectedSalesToAssign.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div>
            <span className="font-medium">{selectedSalesToAssign.length} sales selected</span>
            <span className="text-sm text-gray-600 ml-2">
              (Total: R {unassignedSales
                .filter(s => selectedSalesToAssign.includes(s.id))
                .reduce((sum, s) => sum + (s.total || 0), 0)
                .toFixed(2)})
            </span>
          </div>
          <Button onClick={handleAssignSales} disabled={assigningSales}>
            {assigningSales ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Assign to {formData.name || customer?.name}
          </Button>
        </div>
      )}

      {/* Sales list */}
      {unassignedLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : unassignedSales.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>{salesSearch ? 'No sales found matching your search' : 'No unassigned sales found'}</p>
          <p className="text-sm">{salesSearch ? 'Try a different transaction number' : 'Check "Include sales already assigned" to see all sales'}</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-12">
                  <input
                    type="checkbox"
                    checked={selectedSalesToAssign.length === unassignedSales.length && unassignedSales.length > 0}
                    onChange={selectAllSales}
                    className="h-4 w-4"
                  />
                </TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Transaction #</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Current Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {unassignedSales.map((sale) => (
                <TableRow 
                  key={sale.id} 
                  className={`cursor-pointer hover:bg-gray-50 ${selectedSalesToAssign.includes(sale.id) ? 'bg-blue-50' : ''}`}
                  onClick={() => toggleSaleSelection(sale.id)}
                >
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedSalesToAssign.includes(sale.id)}
                      onChange={() => toggleSaleSelection(sale.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4"
                    />
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {(() => {
                      const raw = sale.sale_date || sale.created_at || sale.date || "";
                      if (!raw) return "—";
                      // sale_date is YYYY-MM-DD, created_at is full ISO
                      return String(raw).slice(0, 10);
                    })()}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{sale.transaction_number}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      sale.transaction_type === 'CASH SALE' ? 'bg-green-100 text-green-700' :
                      sale.transaction_type === 'CARD SALE' ? 'bg-blue-100 text-blue-700' :
                      sale.transaction_type === 'ACCOUNT SALE' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {sale.transaction_type}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {sale.customer_name ? (
                      <span className="text-purple-600">{sale.customer_name}</span>
                    ) : (
                      <span className="text-gray-400 italic">None</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {sale.items?.slice(0, 2).map(i => i.name).join(', ')}
                    {sale.items?.length > 2 && ` +${sale.items.length - 2} more`}
                  </TableCell>
                  <TableCell className="text-right font-medium">R {(sale.total || 0).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
